import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Heading,
    Button,
    Input,
    FormLabel,
    useToast,
    VStack,
    FormControl,
    Flex,
    FormErrorMessage,
    SimpleGrid,
    Select,
} from "@chakra-ui/react";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const AddUser = () => {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [hclientID, setHclientID] = useState(null);
    const toast = useToast();

    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // Form state for new user details
    const [userForm, setUserForm] = useState({
        role: '',
        fname: '',
        sname: '',
        email: '',
        cellNumber: ''
    });

    // Validation state
    const [validationErrors, setValidationErrors] = useState({
        role: '',
        fname: '',
        sname: '',
        email: '',
        cellNumber: ''
    });

    // Resolve logged-in user's HclientID, then fetch roles
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                setLoading(true);

                const profileResponse = await fetch(
                    `${API_BASE_URL}?method=getProfileDets&huserID=${HuserID}&uuid=${UUID}`,
                    { method: "GET" }
                );

                if (!profileResponse.ok) {
                    throw new Error(`HTTP error! status: ${profileResponse.status}`);
                }

                const profileData = await profileResponse.json();
                const clientID = profileData[0]?.HclientID;

                if (!clientID) {
                    throw new Error("Could not determine logged-in user's client");
                }

                setHclientID(clientID);

                const response = await fetch(
                    `${API_BASE_URL}?method=getDDAdminRoles&huserID=${HuserID}&uuid=${UUID}&HclientID=${clientID}`,
                    {
                        method: "GET",
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setRoles(data || []);
            } catch (error) {
                console.error("Error fetching roles:", error);
                toast({
                    title: "Error fetching roles",
                    description: "There was an error loading roles. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
    }, [HuserID, UUID, toast]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserForm(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };


    const validateForm = () => {
        const errors = {};
        let isValid = true;

        if (!userForm.role) {
            errors.role = 'Role is required';
            isValid = false;
        }

        if (!userForm.fname.trim()) {
            errors.fname = 'First name is required';
            isValid = false;
        }

        if (!userForm.sname.trim()) {
            errors.sname = 'Last name is required';
            isValid = false;
        }

        if (!userForm.email.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }

        if (!userForm.cellNumber.trim()) {
            errors.cellNumber = 'Cell number is required';
            isValid = false;
        }

        setValidationErrors(errors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        if (!hclientID) {
            toast({
                title: "Client not loaded",
                description: "Could not determine your client. Please refresh and try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        try {
            setSaving(true);
            const response = await fetch(
                `${API_BASE_URL}?method=addAdminUser&huserID=${HuserID}&uuid=${UUID}&hclientID=${hclientID}`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        role: userForm.role,
                        fname: userForm.fname,
                        sname: userForm.sname,
                        email: userForm.email,
                        cellNumber: userForm.cellNumber
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast({
                title: "User created successfully",
                description: "The new user has been added.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            // Navigate back to users list
            navigate('/users');
        } catch (error) {
            console.error("Error creating user:", error);
            toast({
                title: "Error creating user",
                description: error.message || "There was an error creating the user. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/users');
    };

    return (
        <Box p={5}>
            <Flex align="center" mb={5}>
                <Button onClick={handleCancel} mr={4}>
                    ← Back to Users
                </Button>
                <Heading>Add New User</Heading>
            </Flex>

            <Box
                as="form"
                maxW="1200px"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <VStack spacing={4} align="stretch">
                    {/* Role */}
                    <FormControl isInvalid={!!validationErrors.role} isRequired>
                        <FormLabel>Role</FormLabel>
                        <Select
                            name="role"
                            value={userForm.role}
                            onChange={handleInputChange}
                            placeholder="Select Role"
                            isDisabled={loading}
                        >
                            {roles.map((role) => (
                                <option key={role.ddroleID} value={role.ddroleID}>
                                    {role.roleDesc}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{validationErrors.role}</FormErrorMessage>
                    </FormControl>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                    {/* First Name */}
                    <FormControl isInvalid={!!validationErrors.fname} isRequired>
                        <FormLabel>First Name</FormLabel>
                        <Input
                            name="fname"
                            value={userForm.fname}
                            onChange={handleInputChange}
                            placeholder="First Name"
                        />
                        <FormErrorMessage>{validationErrors.fname}</FormErrorMessage>
                    </FormControl>

                    {/* Last Name */}
                    <FormControl isInvalid={!!validationErrors.sname} isRequired>
                        <FormLabel>Last Name</FormLabel>
                        <Input
                            name="sname"
                            value={userForm.sname}
                            onChange={handleInputChange}
                            placeholder="Last Name"
                        />
                        <FormErrorMessage>{validationErrors.sname}</FormErrorMessage>
                    </FormControl>

                    {/* Email */}
                    <FormControl isInvalid={!!validationErrors.email} isRequired>
                        <FormLabel>Email</FormLabel>
                        <Input
                            name="email"
                            type="email"
                            value={userForm.email}
                            onChange={handleInputChange}
                            placeholder="Email"
                        />
                        <FormErrorMessage>{validationErrors.email}</FormErrorMessage>
                    </FormControl>

                    {/* Cell Number */}
                    <FormControl isInvalid={!!validationErrors.cellNumber} isRequired>
                        <FormLabel>Cell Number</FormLabel>
                        <Input
                            name="cellNumber"
                            type="tel"
                            value={userForm.cellNumber}
                            onChange={handleInputChange}
                            placeholder="Cell Number"
                        />
                        <FormErrorMessage>{validationErrors.cellNumber}</FormErrorMessage>
                    </FormControl>
                </SimpleGrid>

                {/* Action Buttons */}
                <Flex gap={2} mt={6}>
                    <Button
                        type="submit"
                        variant="outline-rounded"
                        isLoading={saving}
                        loadingText="Adding..."
                    >
                        Add User
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
};

export default AddUser;

